import React, { useEffect, useState } from 'react';
import { apiGet } from '../../utils/api';
import { SportIcon } from '../../utils/sportIcons';
import { useTranslation } from '../../contexts/TranslationContext';
import { Sport } from './registerConstants';
import { RegisterFormState } from './registerTypes';

const MAX_SPORTS = 2;

interface RegisterStepSportsProps {
  form: RegisterFormState;
  update: (patch: Partial<RegisterFormState>) => void;
  onContinue: () => void;
  onBack: () => void;
}

const RegisterStepSports: React.FC<RegisterStepSportsProps> = ({ form, update, onContinue, onBack }) => {
  const { t } = useTranslation();
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<Sport[]>('/sports/')
      .then((data) => setSports(data.filter((s) => s.icon)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const isSelected = (sport: Sport) => form.sports.some((s) => s.id === sport.id);

  const toggle = (sport: Sport) => {
    if (isSelected(sport)) {
      update({ sports: form.sports.filter((s) => s.id !== sport.id) });
    } else if (form.sports.length < MAX_SPORTS) {
      update({ sports: [...form.sports, sport] });
    }
  };

  const canContinue = form.sports.length === MAX_SPORTS;

  return (
    <div className="register__card">
      <h1 className="register__title italic-title">{t.register.sportsTitle}</h1>
      <p className="register__subtitle">{t.register.sportsSubtitle}</p>

      {loading ? (
        <p className="register__subtitle">…</p>
      ) : (
        <div className="register__chip-scroller">
          {sports.map((sport) => {
            const selected = isSelected(sport);
            const disabled = !selected && form.sports.length >= MAX_SPORTS;
            return (
              <button
                key={sport.id}
                type="button"
                className={`register__sport-chip${selected ? ' register__sport-chip--active' : ''}`}
                onClick={() => toggle(sport)}
                disabled={disabled}
                aria-pressed={selected}
              >
                <SportIcon name={sport.icon} size={28} />
                <span>{t.sports[sport.name] ?? sport.name}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="register__nav">
        <button type="button" className="register__back" onClick={onBack}>
          {t.register.back}
        </button>
        <button
          type="button"
          className="register__submit button_primary"
          onClick={onContinue}
          disabled={!canContinue}
        >
          {t.register.continue}
        </button>
      </div>
    </div>
  );
};

export default RegisterStepSports;
